import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, trackView, setPageMeta } from "@/lib/api";
import PublicHeader from "@/components/PublicHeader";
import PublicFooter from "@/components/PublicFooter";
import PostListRow from "@/components/PostListRow";
import PostDialog from "@/components/PostDialog";
import WordCloud from "@/components/WordCloud";
import MyWordsDialog from "@/components/MyWordsDialog";
import { Sparkles } from "lucide-react";

const HERO_VIDEO =
  "https://customer-assets.emergentagent.com/job_511b0a6d-8c97-4287-a1b0-bacc69bfd5de/artifacts/tmud0ndv_vecteezy_golden-ears-of-ripe-wheat-sway-against-the-cloudy-sky_13772045.mp4";

const Home = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);
  const [myWordsOpen, setMyWordsOpen] = useState(false);

  useEffect(() => {
    setPageMeta({ url: window.location.href });
    trackView("/");
    (async () => {
      try {
        const { data } = await api.get("/posts", { params: { per_page: 12, page: 1 } });
        setPosts(data.items || []);
      } catch {
        setPosts([]);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <div data-testid="home-page" className="min-h-screen">
      {/* HERO */}
      <section
        data-testid="hero-section"
        className="relative h-screen min-h-[640px] overflow-hidden hero-grain"
      >
        <video className="hero-video" autoPlay muted loop playsInline data-testid="hero-video">
          <source src={HERO_VIDEO} type="video/mp4" />
        </video>
        <div className="absolute inset-0 bg-black/35 z-10" />
        <PublicHeader transparent />
        <div className="relative z-20 h-full flex flex-col items-center justify-center px-6">
          <p className="text-[#f9f8f6]/80 font-sans text-xs sm:text-sm tracking-[0.45em] uppercase mb-8">
            un diario in cammino
          </p>
          <h1
            data-testid="hero-title"
            className="font-serif text-[#f9f8f6] text-6xl sm:text-8xl lg:text-[10vw] tracking-tight leading-none text-center font-light"
            style={{ letterSpacing: "-0.02em" }}
          >
            Attraversando
          </h1>
          <div className="mt-10 w-px h-16 bg-[#f9f8f6]/50" />
          <p className="mt-6 text-[#f9f8f6]/85 font-serif italic text-lg sm:text-xl max-w-2xl text-center leading-relaxed">
            La pazienza delle attese<br />
            attraversando chilometri di vita,<br />
            eccoli i miei attraversamenti<br />
            che in fondo sono pensieri, versi, parole.<br />
            Silenzi
          </p>
        </div>
        <div className="absolute bottom-8 left-0 right-0 z-20 flex justify-center">
          <a
            href="#diario"
            data-testid="hero-scroll"
            className="text-[#f9f8f6]/70 font-sans text-xs tracking-[0.3em] uppercase hover:text-[#f9f8f6] transition-colors"
          >
            ↓ leggi
          </a>
        </div>
      </section>

      {/* DEDICA */}
      <section
        data-testid="dedication-section"
        className="bg-[#1a1918] text-[#f9f8f6] py-16 sm:py-20"
      >
        <div className="max-w-3xl mx-auto px-6 text-center">
          <div className="font-serif text-[#d4b886] text-xl tracking-[0.4em] mb-8">· · ·</div>
          <p className="font-serif italic font-light text-lg sm:text-2xl leading-[1.7] text-[#f9f8f6]">
            Dedicato a chi mi ha conosciuto veramente,<br />
            a chi mi ha voluto bene veramente<br />
            e ha generato in me questi pensieri,<br />
            queste parole.
          </p>
        </div>
      </section>

      {/* DIARIO */}
      <section id="diario" className="relative pt-20 pb-16">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <div className="mb-10 sm:mb-14">
            <p className="font-sans text-xs tracking-[0.3em] uppercase text-[#4a4843] mb-3">
              Diario
            </p>
            <h2 className="font-serif text-3xl sm:text-5xl tracking-tight leading-tight font-light text-[#1a1918] mb-6">
              Ultimi attraversamenti
            </h2>
            <p className="font-serif text-base sm:text-lg italic text-[#4a4843] leading-relaxed max-w-2xl">
              Pensieri raccolti lungo la strada della notte, frammenti di letture, pause sotto un cielo basso, poesie, testi di canzoni. Pubblicati senza fretta, in silenzio, da solo.
            </p>
            <div className="mt-8">
              <button
                type="button"
                data-testid="btn-my-words"
                onClick={() => setMyWordsOpen(true)}
                className="inline-flex items-center gap-2 border border-[#1a1918] bg-[#1a1918] text-[#f9f8f6] hover:bg-[#c5a873] hover:text-[#1a1918] hover:border-[#c5a873] transition-colors px-5 py-3 font-sans text-[10px] sm:text-xs tracking-[0.3em] uppercase"
              >
                <Sparkles size={14} /> le mie parole
              </button>
            </div>
          </div>

          {loading ? (
            <div data-testid="loading" className="font-sans text-sm text-[#4a4843]">caricamento…</div>
          ) : posts.length === 0 ? (
            <div data-testid="empty-state" className="border border-[#e5e1d8] p-10 text-center">
              <p className="font-serif text-2xl text-[#1a1918] mb-2">Nessun post pubblicato</p>
              <p className="font-sans text-sm text-[#4a4843]">
                Torna presto — il diario sta per iniziare.
              </p>
            </div>
          ) : (
            <ul className="divide-y divide-[#e5e1d8] border-t border-b border-[#e5e1d8]">
              {posts.map((p) => (
                <li key={p.id} data-testid={`post-card-${p.id}`}>
                  <PostListRow post={p} onOpen={setOpenId} />
                </li>
              ))}
            </ul>
          )}

          <div className="mt-12 flex justify-center">
            <Link
              to="/archivio"
              data-testid="link-archive"
              className="font-sans text-xs tracking-[0.3em] uppercase border-b border-[#1a1918] pb-1 text-[#1a1918] hover:text-[#4a4843] hover:border-[#4a4843] transition-colors"
            >
              Esplora l'archivio →
            </Link>
          </div>
        </div>
      </section>

      <PostDialog postId={openId} open={!!openId} onOpenChange={(v) => !v && setOpenId(null)} />
      <MyWordsDialog
        open={myWordsOpen}
        onOpenChange={setMyWordsOpen}
        onPickPost={(id) => setOpenId(id)}
      />
      <WordCloud />
      <PublicFooter />
    </div>
  );
};

export default Home;
